import { useState, useEffect, useRef } from 'react'
import { Plus, Trash2, AlertTriangle, CheckCircle2 } from 'lucide-react'
import clientesService from '../../services/clientesService'

function CompradorRow({
  item, index, isPrincipal, mostrarPercentual, organizadorId, disabled,
  onSelecionarComprador, onChangeFazenda, onChangePercentual, onRemove, podeRemover,
}) {
  const [search, setSearch] = useState(item.comprador_nome || '')
  const [opcoes, setOpcoes] = useState([])
  const [fazendas, setFazendas] = useState([])
  const debounceRef = useRef(null)

  useEffect(() => {
    setSearch(item.comprador_nome || '')
  }, [item.comprador_id, item.comprador_nome])

  // Carrega as fazendas do cliente selecionado (cada co-comprador com a sua)
  useEffect(() => {
    let vivo = true
    if (!item.comprador_id) { setFazendas([]); return }
    ;(async () => {
      try {
        const cliente = await clientesService.getById(organizadorId, item.comprador_id)
        if (vivo) setFazendas(cliente?.fazendas || [])
      } catch (e) { console.error(e) }
    })()
    return () => { vivo = false }
  }, [item.comprador_id, organizadorId])

  const buscar = (termo) => {
    setSearch(termo)
    clearTimeout(debounceRef.current)
    if (!termo || termo.length < 2) { setOpcoes([]); return }
    debounceRef.current = setTimeout(async () => {
      try {
        const data = await clientesService.getByOrganizador(organizadorId, 1, 20, termo)
        setOpcoes(Array.isArray(data) ? data : (data?.data || []))
      } catch (e) { console.error(e) }
    }, 300)
  }

  const selecionar = (cliente) => {
    onSelecionarComprador({ comprador_id: cliente.id, comprador_nome: cliente.nome, fazenda_comprador_id: '' })
    setSearch(cliente.nome)
    setOpcoes([])
  }

  return (
    <div className="flex items-start gap-2">
      <div className="relative flex-1">
        <input
          type="text" value={search} disabled={disabled || isPrincipal}
          onChange={(e) => buscar(e.target.value)}
          onBlur={() => setTimeout(() => setOpcoes([]), 150)}
          className={`input-rematei text-sm ${(disabled || isPrincipal) ? 'bg-gray-100 cursor-not-allowed' : ''}`}
          placeholder="Digite o nome do comprador..."
        />
        {isPrincipal && (
          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] uppercase tracking-wide text-gray-400 pointer-events-none">
            principal
          </span>
        )}
        {opcoes.length > 0 && (
          <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
            {opcoes.map(c => (
              <li key={c.id} onMouseDown={() => selecionar(c)} className="px-3 py-2 text-sm hover:bg-blue-50 cursor-pointer">
                {c.nome}
              </li>
            ))}
          </ul>
        )}
      </div>

      <select
        value={item.fazenda_comprador_id || ''}
        disabled={disabled || !item.comprador_id}
        onChange={(e) => onChangeFazenda(e.target.value)}
        className={`input-rematei text-sm w-44 flex-shrink-0 ${(disabled || !item.comprador_id) ? 'bg-gray-100 cursor-not-allowed' : ''}`}
      >
        <option value="">Fazenda…</option>
        {fazendas.map(f => (
          <option key={f.id} value={f.id}>{f.nome}</option>
        ))}
      </select>

      {mostrarPercentual && (
        <div className="relative w-24 flex-shrink-0">
          <input
            type="number" min="0" max="100" step="0.01" disabled={disabled || isPrincipal}
            value={item.percentual}
            onChange={(e) => onChangePercentual(e.target.value)}
            className={`input-rematei text-sm pr-6 ${(disabled || isPrincipal) ? 'bg-gray-100 cursor-not-allowed' : ''}`}
            title={isPrincipal ? 'Ajusta automaticamente conforme a % dos outros compradores' : undefined}
          />
          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">%</span>
        </div>
      )}

      {podeRemover && !disabled && (
        <button type="button" onClick={onRemove} className="p-2 text-red-600 hover:bg-red-50 rounded-lg" title="Remover comprador">
          <Trash2 className="w-4 h-4" />
        </button>
      )}
    </div>
  )
}

/**
 * Lista editável de compradores de um lote (fluxo cavalo — lote pode ser vendido
 * para N compradores, cada um com sua % de participação e sua própria fazenda).
 * Controlado: recebe `compradores` (índice 0 = principal, travado no comprador
 * escolhido acima) e notifica mudanças via `onChange`.
 */
export default function CompradoresCavaloEditor({ compradores, onChange, organizadorId, disabled }) {
  const soma = compradores.reduce((s, c) => s + (parseFloat(c.percentual) || 0), 0)
  const multi = compradores.length > 1

  const adicionar = () => {
    onChange([...compradores, { comprador_id: '', comprador_nome: '', fazenda_comprador_id: '', percentual: '' }])
  }

  const remover = (index) => {
    let novos = compradores.filter((_, i) => i !== index)
    // Ao voltar para 1 comprador, o principal reassume 100%
    if (novos.length === 1) novos = novos.map(c => ({ ...c, percentual: 100 }))
    onChange(novos)
  }

  const atualizarSelecionado = (index, dados) => {
    onChange(compradores.map((c, i) => i === index ? { ...c, ...dados } : c))
  }

  const atualizarFazenda = (index, valor) => {
    onChange(compradores.map((c, i) => i === index ? { ...c, fazenda_comprador_id: valor } : c))
  }

  // Editar a % de um co-comprador secundário (índice > 0) faz o principal
  // (índice 0) absorver o restante pra fechar 100% — mesma lógica do editor
  // de vendedores.
  const atualizarPercentual = (index, valor) => {
    let novos = compradores.map((c, i) => i === index ? { ...c, percentual: valor } : c)
    if (index !== 0 && novos.length > 1) {
      const somaOutros = novos.slice(1).reduce((s, c) => s + (parseFloat(c.percentual) || 0), 0)
      const restante = Math.max(0, Math.round((100 - somaOutros) * 100) / 100)
      novos = novos.map((c, i) => i === 0 ? { ...c, percentual: restante } : c)
    }
    onChange(novos)
  }

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-sm font-medium text-gray-700">Comprador(es)</label>
        <p className="text-xs text-gray-500">Divida a compra entre 2 ou mais compradores, cada um com sua % e sua fazenda</p>
      </div>

      {compradores.map((item, index) => (
        <CompradorRow
          key={index}
          item={item}
          index={index}
          isPrincipal={index === 0 && multi}
          mostrarPercentual={multi}
          organizadorId={organizadorId}
          disabled={disabled}
          onSelecionarComprador={(dados) => atualizarSelecionado(index, dados)}
          onChangeFazenda={(valor) => atualizarFazenda(index, valor)}
          onChangePercentual={(valor) => atualizarPercentual(index, valor)}
          onRemove={() => remover(index)}
          podeRemover={multi}
        />
      ))}

      <div className="flex items-center justify-between">
        {!disabled && (
          <button
            type="button"
            onClick={adicionar}
            className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            <Plus className="w-4 h-4" /> Adicionar comprador
          </button>
        )}

        {multi && (
          <span className={`flex items-center gap-1 text-xs font-medium ${
            Math.abs(soma - 100) < 0.01 ? 'text-green-600' : 'text-amber-600'
          }`}>
            {Math.abs(soma - 100) < 0.01
              ? <CheckCircle2 className="w-3.5 h-3.5" />
              : <AlertTriangle className="w-3.5 h-3.5" />}
            Total: {soma.toFixed(2)}%
          </span>
        )}
      </div>
    </div>
  )
}
